// utils/geojsonHelpers.js
import proj4 from 'proj4'

// Define UTM Zone 17S (Ecuador main zone)
proj4.defs('EPSG:32717', '+proj=utm +zone=17 +south +datum=WGS84 +units=m +no_defs')

/**
 * Verifica si las coordenadas parecen ser proyectadas (no WGS84)
 * Si x > 180 o y > 90, asumimos que son metros (UTM u otro)
 */
const isProjected = (coords) => {
  if (!Array.isArray(coords) || coords.length < 2) return false
  return Math.abs(coords[0]) > 180 || Math.abs(coords[1]) > 90
}

/**
 * Recorre y transforma las coordenadas recursivamente
 */
const transformCoords = (coords) => {
  if (!Array.isArray(coords)) return coords

  // Si es un par de coordenadas [x, y]
  if (coords.length >= 2 && typeof coords[0] === 'number') {
    if (isProjected(coords)) {
      // Convertir de UTM 17S a WGS84
      return proj4('EPSG:32717', 'EPSG:4326', coords)
    }
    return coords
  }

  // Si es un array de arrays, seguir bajando
  return coords.map(transformCoords)
}

/**
 * Convierte un GeoJSON con coordenadas proyectadas (UTM) a WGS84
 */
export const convertToWGS84 = (geojson) => {
  if (!geojson) return geojson

  try {
    const cloned = typeof geojson === 'string' ? JSON.parse(geojson) : JSON.parse(JSON.stringify(geojson))

    const processGeometry = (geometry) => {
      if (!geometry) return

      if (geometry.type === 'GeometryCollection') {
        geometry.geometries.forEach(processGeometry)
      } else if (geometry.coordinates) {
        geometry.coordinates = transformCoords(geometry.coordinates)
      }
    }

    if (cloned.type === 'FeatureCollection') {
      cloned.features.forEach(f => processGeometry(f.geometry))
    } else if (cloned.type === 'Feature') {
      processGeometry(cloned.geometry)
    } else {
      processGeometry(cloned)
    }

    return cloned
  } catch (err) {
    console.error('Error convirtiendo coordenadas:', err)
    return geojson
  }
}

/**
 * Obtiene el valor de un atributo del árbol, manejando ambas estructuras
 */
export const getTreeAttribute = (properties, key) => {
  // Primero intentar acceso directo
  if (properties[key] !== undefined && properties[key] !== null) {
    return properties[key]
  }

  // Buscar en attributes array
  if (properties.attributes?.length) {
    const attr = properties.attributes.find(a => a.metaFeature?.key === key)
    return attr?.value ?? null
  }

  return null
}

/**
 * Normaliza todas las propiedades del árbol al formato plano
 */
export const normalizeTreeProperties = properties => {
  if (!properties.attributes?.length) {
    return properties // Ya está normalizado
  }

  const normalized = { ...properties }

  // Extraer valores de attributes al nivel raíz
  properties.attributes.forEach(attr => {
    if (attr.metaFeature?.key && attr.value !== undefined) {
      normalized[attr.metaFeature.key] = attr.value
    }
  })

  return normalized
}

export const resolveBooleanValue = value => {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value === 1
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase()
    if (['1', 'true', 't', 'si', 'sí', 'yes'].includes(normalized)) return true
    if (['0', 'false', 'f', 'no'].includes(normalized)) return false
  }
  if (Array.isArray(value)) return Boolean(value[0])
  if (value && typeof value === 'object') {
    if (Array.isArray(value.data)) return Boolean(value.data[0])
    if (value.value !== undefined) return resolveBooleanValue(value.value)
  }
  return Boolean(value)
}

const firstDefined = values =>
  values.find(value => value !== undefined && value !== null)

export const getFeatureEnabledValue = feature => {
  const properties = feature?.properties || {}
  const tree = properties.tree || properties.arbol || feature?.tree || feature?.arbol || {}
  const value = firstDefined([
    properties.habilitado,
    properties.enabled,
    properties.activo,
    properties.treeHabilitado,
    properties.tree_enabled,
    tree.habilitado,
    tree.enabled,
    feature?.habilitado,
    feature?.enabled,
    feature?.activo,
  ])

  return value === undefined ? undefined : resolveBooleanValue(value)
}

export const filterEnabledGeojson = (geojson, enabledOnly = true) => {
  const featureCollection = {
    ...(geojson || {}),
    type: geojson?.type || 'FeatureCollection',
    features: Array.isArray(geojson?.features) ? geojson.features : [],
  }

  if (!enabledOnly) return featureCollection

  return {
    ...featureCollection,
    features: featureCollection.features.filter(feature => {
      const enabled = getFeatureEnabledValue(feature)
      return enabled === undefined ? true : enabled
    }),
  }
}
